// src/lib/useSort.ts
//
// One small hook for a table that sorts on the SERVER, not in the browser —
// see BillsListPage/AuditPage's own comments for why: the gateway caps both
// `/api/bills` and `/api/audit` well below their real row counts, so a
// client-side sort would silently sort only the fetched page while looking
// exactly like a working sort.
//
// `column: null` IS the server's own default order — packages/persistence's
// `buildBillOrderBy`/`buildAuditOrderBy` already have one whitelisted
// default, so "not sorted yet" here must mean exactly that, never a
// client-invented one. That is also the state a third click returns to.
import { useCallback, useState } from 'react'

export type SortDirection = 'asc' | 'desc'

export interface SortState<K extends string> {
  readonly column: K | null
  readonly dir: SortDirection
}

const DEFAULT_SORT: SortState<never> = { column: null, dir: 'asc' }

export interface UseSortResult<K extends string> {
  readonly sort: SortState<K>
  /** Ascending -> descending -> back to the server's default order. */
  readonly onSort: (column: K) => void
  /**
   * True once the user has clicked any header in this table.
   *
   * Needed because the live region that announces the new order must stay
   * SILENT on first paint — an `aria-live` region that has content when it is
   * first inserted announces nothing anyway, but one that is populated on the
   * initial render and then repopulated identically can double-announce. More
   * importantly, the third click returns to `column: null`, which is
   * indistinguishable from "never sorted" by the sort state alone: without
   * this flag the announcement for "back to the default order" would be an
   * empty string and the change would go unspoken.
   */
  readonly interacted: boolean
}

export function useSort<K extends string>(): UseSortResult<K> {
  const [sort, setSort] = useState<SortState<K>>(DEFAULT_SORT)
  const [interacted, setInteracted] = useState(false)

  const onSort = useCallback((column: K) => {
    setInteracted(true)
    setSort((previous) => {
      if (previous.column !== column) return { column, dir: 'asc' }
      if (previous.dir === 'asc') return { column, dir: 'desc' }
      return DEFAULT_SORT
    })
  }, [])

  return { sort, onSort, interacted }
}

/**
 * The sentence a sortable table's `aria-live` region should carry, or `''` for
 * silence.
 *
 * ⚠ `busy` must be the table's in-flight flag. The order is decided by the
 * SERVER (`useApi(() => api.bills(sortParams(sort)))`), so between the click
 * and the response the rows on screen are still in the OLD order and the row
 * count is the old count. Announcing then would state a change that has not
 * happened yet, and would announce a second time when it did. Returning `''`
 * while busy also clears the region, so the settled sentence is a genuine
 * change and is spoken exactly once.
 */
export function sortAnnouncement<K extends string>(
  sort: SortState<K>,
  interacted: boolean,
  busy: boolean,
  columnLabels: Readonly<Record<K, string>>,
  rowCount: number,
): string {
  if (!interacted || busy) return ''
  const rows = `${rowCount} ${rowCount === 1 ? 'row' : 'rows'} shown.`
  if (sort.column === null) return `Sort cleared. Back to the default order. ${rows}`
  return `Sorted by ${columnLabels[sort.column]}, ${sort.dir === 'asc' ? 'ascending' : 'descending'}. ${rows}`
}

/** `{ sort, dir }` for `api.bills()`/`api.audit()` — omitted entirely when unsorted, never sent as `undefined`. */
export function sortParams<K extends string>(sort: SortState<K>): { sort?: K; dir?: SortDirection } {
  return sort.column === null ? {} : { sort: sort.column, dir: sort.dir }
}
